/**
 * RFC-149: Marketing UTM helper.
 *
 * Appends standardized UTM params to outbound marketing URLs and stores
 * attribution in sessionStorage for cross-domain navigations to /auth*
 * (marketing and app are co-hosted on lekalearning.com, so internal SPA
 * route changes don't naturally carry query strings).
 *
 * Idempotent: calling twice with the same params does not double-append.
 */

export interface UtmParams {
  medium: string; // page slug, e.g. 'landing', 'pricing', 'schools'
  campaign: string; // CTA slot, e.g. 'hero-primary', 'pricing-table', 'footer'
}

export const UTM_SOURCE = 'marketing';
export const ATTRIBUTION_STORAGE_KEY = 'leka.lastMarketingAttribution';
export const ATTRIBUTION_TTL_MS = 30 * 60 * 1000; // 30 minutes

export interface StoredAttribution {
  utm_source: string;
  utm_medium: string;
  utm_campaign: string;
  ts: number;
}

/**
 * Append UTM params to a URL, preserving existing query/hash and avoiding
 * duplicates when called repeatedly.
 *
 * - Absolute URLs (http(s)://...) are parsed via URL().
 * - Relative paths (/foo, foo, /foo?bar) are handled with a manual parse so
 *   we don't accidentally rebase against window.location.
 */
export function withUtm(url: string, params: UtmParams): string {
  const utm = {
    utm_source: UTM_SOURCE,
    utm_medium: params.medium,
    utm_campaign: params.campaign,
  };

  // Absolute URL: use URL parsing
  if (/^https?:\/\//i.test(url)) {
    try {
      const u = new URL(url);
      for (const [key, value] of Object.entries(utm)) {
        u.searchParams.set(key, value);
      }
      return u.toString();
    } catch {
      // Fall through to manual handling
    }
  }

  // Relative path / fragment-bearing URL
  const [pathAndQuery, hash = ''] = url.split('#', 2);
  const [path, query = ''] = pathAndQuery.split('?', 2);
  const search = new URLSearchParams(query);
  for (const [key, value] of Object.entries(utm)) {
    search.set(key, value);
  }
  const qs = search.toString();
  const hashPart = hash ? `#${hash}` : '';
  return qs ? `${path}?${qs}${hashPart}` : `${path}${hashPart}`;
}

/**
 * Persist UTM attribution to sessionStorage so the signup handler can read
 * it after an internal SPA navigation. Wrapped in try/catch — Safari private
 * mode can throw on sessionStorage access; failure must never block navigation.
 */
export function persistAttribution(params: UtmParams): void {
  try {
    if (typeof window === 'undefined' || !window.sessionStorage) return;
    const payload: StoredAttribution = {
      utm_source: UTM_SOURCE,
      utm_medium: params.medium,
      utm_campaign: params.campaign,
      ts: Date.now(),
    };
    window.sessionStorage.setItem(ATTRIBUTION_STORAGE_KEY, JSON.stringify(payload));
  } catch {
    // Safari private mode / disabled storage — silently ignore.
  }
}

/**
 * Read persisted attribution if still within TTL. Returns null if missing,
 * malformed, or expired. Callers (e.g. signup handler) use this at conversion
 * time to attribute the new account.
 */
export function readAttribution(now: number = Date.now()): StoredAttribution | null {
  try {
    if (typeof window === 'undefined' || !window.sessionStorage) return null;
    const raw = window.sessionStorage.getItem(ATTRIBUTION_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as StoredAttribution;
    if (
      typeof parsed?.ts !== 'number' ||
      typeof parsed?.utm_source !== 'string' ||
      typeof parsed?.utm_medium !== 'string' ||
      typeof parsed?.utm_campaign !== 'string'
    ) {
      return null;
    }
    if (now - parsed.ts > ATTRIBUTION_TTL_MS) return null;
    return parsed;
  } catch {
    return null;
  }
}

export function clearAttribution(): void {
  try {
    if (typeof window === 'undefined' || !window.sessionStorage) return;
    window.sessionStorage.removeItem(ATTRIBUTION_STORAGE_KEY);
  } catch {
    // ignore
  }
}
